import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Users, Briefcase, FileText, DollarSign, Mail,
  Settings, Calculator, Zap, Smartphone
} from 'lucide-react';
import { useBusinessContext } from '../../contexts/BusinessContext';

const GettingStartedGuide = () => {
  const navigate = useNavigate();
  const { isSimpleMode } = useBusinessContext();

  // Which version of the workflow to show. Starts on the mode the business is currently using.
  const [mode, setMode] = useState(isSimpleMode ? 'simple' : 'standard');

  // Mark as viewed when component mounts
  useEffect(() => {
    localStorage.setItem('suddyflow_viewed_getting_started', 'true');
  }, []);

  // ---------- Steps everyone does first ----------
  const stepsBefore = [
    {
      short: 'Set up business profile',
      title: 'Set Up Your Business Profile',
      icon: Settings,
      description: 'Complete your business information so it appears on invoices and customer communications.',
      actions: [
        'Go to Settings → Business',
        'Click Edit, then add your business name, address, phone, and email',
        'Go to Settings → Branding to upload your logo (optional)'
      ],
      tips: 'This information appears on every invoice and message you send to customers.'
    },
    {
      short: 'Choose how you work',
      title: 'Choose How You Work',
      icon: Zap,
      description: 'Pick the version of SuddyFlow that fits your business. You can change it any time without losing anything.',
      actions: [
        'Go to Settings → Business and find the "How You Work" card at the top',
        'Choose Simple if you agree on price and date with the customer yourself and just need professional invoices and tax tracking',
        'Choose Standard if you send quotes, schedule work, and use leads, agreements, or a team'
      ],
      tips: 'Not sure? Start with Simple. Switching to Standard later brings back quotes, leads, and the rest of the menu, and nothing you created is deleted.'
    },
    {
      short: 'Configure tax settings',
      title: 'Configure Tax Settings',
      icon: Calculator,
      description: "Set up sales tax collection and configure tax reminders so you're always prepared.",
      actions: [
        'Go to Settings → Tax',
        "Toggle 'Collect Sales Tax' ON if required in your state",
        'Select your state and county for automatic rate lookup',
        'Enable quarterly tax reminders to never miss a deadline',
        'Save your settings'
      ],
      tips: "Most states require sales tax on service businesses. Check with your state's Department of Revenue if unsure. SuddyFlow automatically calculates the correct rate for NC counties!"
    },
    {
      short: 'Add customer',
      title: 'Add Your First Customer',
      icon: Users,
      description: 'Before you can create a job or invoice, you need at least one customer in the system.',
      actions: [
        'Go to Customers page',
        "Click '+ Add Customer'",
        'Enter customer name and address (required)',
        'Add a phone number so you can text them, and an email so you can email them',
        'Save the customer'
      ],
      tips: 'A phone number lets you text quotes and invoices. An email address lets you email them and give portal access. Add whichever your customer prefers.'
    },
    {
      short: 'Set up service types',
      title: 'Set Up Service Types',
      icon: Briefcase,
      description: 'Define your services and pricing before creating jobs. This makes quoting faster and more consistent.',
      actions: [
        'Go to Settings → Service Types',
        'Review pre-configured services from your industry',
        'Adjust prices to match your rates',
        'Add any additional services you offer',
        "Remove any that don't apply to your business"
      ],
      tips: "We've pre-loaded service types based on your industry selection. Customize them to match your actual pricing!"
    }
  ];

  // ---------- Simple mode: job to paid ----------
  const stepsSimple = [
    {
      short: 'Add job',
      title: 'Add a Job',
      icon: Briefcase,
      description: 'A job is the work you do for a customer. Add one once you and the customer agree on the work and the price.',
      actions: [
        "Go to Jobs and click '+ New Job'",
        'Select the customer',
        'Choose the service and enter the price you agreed on',
        "Confirm the work date (today's date is filled in for you)",
        'Save the job'
      ],
      tips: "In Simple mode the job starts as Scheduled, so there's no quote step and no status to pick."
    },
    {
      short: 'Complete job',
      title: 'Complete the Job',
      icon: CheckCircle,
      description: 'When the work is done, tap Complete. This is also where you record payment if the customer paid you.',
      actions: [
        "Go to Jobs and open your job, then click the green 'Complete' button",
        'Adjust the final amount if the price changed',
        "If the customer paid, check 'Yes, customer already paid' and pick how (cash, check, Venmo, and so on)",
        "Click 'Complete & Create Invoice', or 'Complete & Record Payment' if they paid"
      ],
      tips: "Paid on the spot? You get a Paid invoice and the payment is recorded. Not paid yet? You get an Unpaid invoice, due in 14 days. Either way you don't have to create the invoice yourself."
    },
    {
      short: 'Send invoice',
      title: 'Send or Print the Invoice',
      icon: FileText,
      description: 'Right after you complete the job, your invoice is ready to send.',
      actions: [
        "On the 'Job Completed' screen, tap Text Invoice, Download / Print PDF, or Email Invoice",
        'Text Invoice sends the customer a link to view their invoice. It needs a phone number on the customer and texting turned on in Settings → Business',
        'Download / Print PDF gives you a paper copy to hand over',
        'Closed the screen? Open Invoices, tap the invoice, and use the same buttons'
      ],
      tips: 'Texting the invoice before you leave the job is the fastest way to get paid, and it works for customers who never use email.'
    },
    {
      short: 'Get paid',
      title: 'Get Paid',
      icon: DollarSign,
      description: "If the customer didn't pay on the spot, record the payment when it arrives.",
      actions: [
        'Go to Invoices and tap the invoice',
        "Click 'Record Payment'",
        'Enter the amount, date, and method (Cash, Check, Venmo, and so on)',
        "Click 'Record Payment'"
      ],
      tips: "The invoice changes to 'Paid' once the full amount is recorded. Partial payments are fine, and the invoice shows what's still owed."
    }
  ];

  // ---------- Standard mode: job to paid ----------
  const stepsStandard = [
    {
      short: 'Create job',
      title: 'Create a Job',
      icon: Briefcase,
      description: 'Jobs represent the work you do for customers. Create one when a customer requests service.',
      actions: [
        'Go to Jobs page',
        "Click '+ New Job'",
        'Select the customer',
        'Add services from your service types menu',
        "Set status to 'Quoted' (for estimates) or 'Scheduled' (if approved)",
        'Set schedule date (if known)',
        'Save the job'
      ],
      tips: "Use 'Quoted' for estimates you send to customers, then tap Email Quote or Text Quote on the job. Once they accept, it becomes a scheduled job."
    },
    {
      short: 'Complete job',
      title: 'Complete the Job',
      icon: CheckCircle,
      description: 'Once the work is finished, mark the job as completed to track your work and prepare for invoicing.',
      actions: [
        'Go to Jobs page',
        'Find your scheduled or in-progress job',
        "Click the green 'Complete' button on the job card",
        'Review and adjust the final amount if it differs from the quote',
        'Confirm the completion date (defaults to today)',
        'Add any completion notes (optional)',
        'If the customer paid on the spot, check that box and choose the payment method',
        "Click 'Complete Job'"
      ],
      tips: "The completion form captures the final amount, date, and notes in one step. If the customer didn't pay yet, the job appears in 'Ready to Invoice'."
    },
    {
      short: 'Create invoice',
      title: 'Create an Invoice',
      icon: FileText,
      description: 'Turn your completed job into an invoice so you can get paid.',
      actions: [
        'Go to Invoices page',
        "Look for the green 'Ready to Invoice' section at the top",
        "Click 'Create Invoice' next to your completed job",
        'Review pre-filled amounts (subtotal, tax, total)',
        'Set due date (defaults to 14 days)',
        'Check the box to send email (if customer has email)',
        "Click 'Create Invoice'"
      ],
      tips: 'Everything is pre-filled, including tax if enabled. After creating it, open the invoice any time to Text, Email, or Download a PDF.'
    },
    {
      short: 'Get paid',
      title: 'Get Paid',
      icon: DollarSign,
      description: 'Record payments when customers pay you.',
      actions: [
        'Go to Invoices page',
        'Find the invoice you created',
        "Click 'Record Payment'",
        'Enter payment amount and date',
        'Select payment method (Cash, Check, Venmo, and so on)',
        'Check the box to send a receipt email',
        "Click 'Record Payment'"
      ],
      tips: "Invoices update to 'Paid' once the full amount is recorded. If you've connected card payments, customers can also pay online from the link in their text or email."
    }
  ];

  // ---------- Optional last step ----------
  const stepsAfter = [
    {
      short: 'Customer portal (optional)',
      title: 'Give Customers Portal Access (Optional)',
      icon: Mail,
      description: 'Let customers view their jobs and invoices online anytime.',
      actions: [
        'Go to Customers page',
        'Find a customer with an email address',
        "Click 'Send Portal Access' button",
        'Customer receives an email with a secure link',
        'They can view all their jobs and invoices online'
      ],
      tips: 'Portal access links are secure and expire after 1 hour. Customers can request new links anytime!'
    }
  ];

  const steps = [
    ...stepsBefore,
    ...(mode === 'simple' ? stepsSimple : stepsStandard),
    ...stepsAfter
  ].map((step, i) => ({ ...step, number: i + 1 }));

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Getting Started with SuddyFlow</h1>
          <p className="text-lg text-gray-600">
            Follow these {steps.length} steps to go from setup to getting paid. Your first invoice can go out in minutes!
          </p>
        </div>

        {/* Mode picker */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-1">How do you work?</h2>
          <p className="text-sm text-gray-600 mb-4">
            Pick the one that sounds like you. The steps below change to match. You choose your real setting in
            Settings → Business → How You Work, and you can switch any time.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => setMode('simple')}
              className={`text-left rounded-lg border-2 p-4 transition-colors ${
                mode === 'simple' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                <Zap size={18} className="text-green-600" /> Simple
                {mode === 'simple' && <CheckCircle size={16} className="text-green-600 ml-auto" />}
              </div>
              <p className="text-sm text-gray-600">
                I agree on the price and date with the customer myself. I just need professional invoices and tax tracking.
              </p>
            </button>
            <button
              onClick={() => setMode('standard')}
              className={`text-left rounded-lg border-2 p-4 transition-colors ${
                mode === 'standard' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                <FileText size={18} className="text-blue-600" /> Standard
                {mode === 'standard' && <CheckCircle size={16} className="text-blue-600 ml-auto" />}
              </div>
              <p className="text-sm text-gray-600">
                I send quotes, schedule work, and want leads, agreements, and team features.
              </p>
            </button>
          </div>
        </div>

        {/* Quick Summary Card */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            The Complete Workflow ({mode === 'simple' ? 'Simple' : 'Standard'})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-2 text-sm text-gray-700">
            {steps.map((step) => (
              <div key={step.number} className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {step.number}
                </span>
                <span>{step.short}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="space-y-8">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Step Header */}
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Icon size={24} className="text-primary-600" />
                        {step.title}
                      </h2>
                      <p className="text-gray-700 mt-1">{step.description}</p>
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className="p-6">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
                    How to do it:
                  </h3>
                  <ol className="space-y-2 mb-4">
                    {step.actions.map((action, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{action}</span>
                      </li>
                    ))}
                  </ol>

                  {/* Pro Tip */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold text-sm">💡 Pro Tip:</span>
                      <p className="text-sm text-blue-900">{step.tips}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Texting callout */}
        <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Smartphone className="text-green-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Want to text quotes and invoices?</h2>
              <p className="text-sm text-gray-700 mb-3">
                Go to Settings → Business → SMS Notifications. Save your mobile number, turn on SMS Notifications, and
                check "Text Quotes &amp; Invoices to Customers". Then any customer with a phone number can get a quote or
                invoice by text.
              </p>
              <button
                onClick={() => navigate('/help/sms-notifications')}
                className="text-sm font-medium text-green-700 hover:text-green-800"
              >
                Read the Text Messages guide →
              </button>
            </div>
          </div>
        </div>

        {/* Common Questions */}
        <div className="mt-12 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Common Questions</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Should I use Simple or Standard?
              </h3>
              <p className="text-gray-600 text-sm">
                If you already agree on the price and date with the customer and just want professional invoices and tax
                records, use Simple. If you send quotes, take online quote acceptances, or use leads, agreements, or a team,
                use Standard. You can switch any time in Settings → Business → How You Work, and nothing is deleted.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if I don't have my customer's email address?
              </h3>
              <p className="text-gray-600 text-sm">
                No problem! You can still create customers, jobs, and invoices. If they have a phone number, you can text
                the invoice. If not, download the PDF and print it. You can always add their email or phone later.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I skip the "Quoted" status?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes! If a customer approves work immediately, create the job as Scheduled right away. In Simple mode this is
                automatic. "Quoted" is just for tracking estimates you've sent that are waiting for approval.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What's the difference between Quote Amount and Final Amount?
              </h3>
              <p className="text-gray-600 text-sm">
                Quote Amount is your initial estimate. Final Amount is what you actually charge after completing
                the job. They're often the same, but sometimes you discover extra work or issues that change the
                price. The invoice uses the Final Amount (or Quote Amount if Final isn't set).
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                How do customers pay me?
              </h3>
              <p className="text-gray-600 text-sm">
                Most customers pay you directly (cash, check, Venmo, Zelle) and you record it in SuddyFlow. If you've
                connected card payments in Settings → Payments, customers can also pay by card from the link in their
                text or email.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Do I need to set up sales tax?
              </h3>
              <p className="text-gray-600 text-sm">
                It depends on your state and service type. Most states require sales tax on service businesses.
                Go to Settings → Tax to configure your tax rate. If you're unsure whether your services are taxable,
                check with your state's Department of Revenue or a tax professional.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I delete a job or invoice after creating it?
              </h3>
              <p className="text-gray-600 text-sm">
                An invoice can be deleted only if it has no payments recorded, and deleting it sends the job back to
                "Ready to Invoice". A job that has a payment recorded can't be deleted either. This keeps your accounting
                records correct. If you made a mistake on a paid job, record a refund or credit instead.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                I completed a job with the wrong amount. What now?
              </h3>
              <p className="text-gray-600 text-sm">
                If it has no invoice yet, open the job, click Edit, and choose Reopen job. Fix the price and complete it
                again. If it has an unpaid invoice, delete the invoice first, then reopen the job.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What are the pre-loaded service types?
              </h3>
              <p className="text-gray-600 text-sm">
                When you signed up, we pre-loaded service types based on your industry selection. These include
                common services with suggested pricing. You can customize names, prices, and descriptions, or
                add your own services in Settings → Service Types.
              </p>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-8 text-center border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            You're Ready to Go! 🎉
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            That's it! You now know the complete workflow from getting a new customer to getting paid.
            Start with Step 1 and work your way through. You'll have your first invoice sent in no time!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/settings')}
              className="btn btn-primary"
            >
              Go to Settings
            </button>
            <button
              onClick={() => navigate('/customers')}
              className="btn btn-secondary"
            >
              Add Your First Customer
            </button>
          </div>
        </div>

        {/* Back to Help Center */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedGuide;